/**
 * The stable error codes of the design 5.1 envelope.
 *
 * "`code` is a stable, documented string ... Every code is enumerated in
 * `packages/domain/src/error/code.ts` and the spec's `components/schemas/ErrorCode` is
 * generated from it, so a new code cannot ship undocumented." (design 5.1)
 *
 * A code is never removed or re-spelled once published: a consumer branches on it.
 */
export const ERROR_CODE = [
    // ---- configuration (design 9.1) — the process exits; these never reach a response
    "configuration_missing",
    "configuration_invalid",
    "measured_constant_unset",
    "measured_constant_provisional",
    // ---- validation (400)
    "validation_failed",
    "malformed_e164",
    "malformed_sip_uri",
    "password_too_short",
    "unsupported_media_type",
    "payload_too_large",
    "idempotency_key_reused_with_different_body",
    "cursor_invalid",
    // ---- authentication (401)
    "authentication_required",
    "credential_invalid",
    "auth_session_expired",
    "auth_session_revoked",
    "csrf_token_invalid",
    "mfa_required",
    "mfa_factor_invalid",
    "sso_required_for_domain",
    "api_key_revoked",
    // ---- authorisation (403)
    "permission_denied",
    "scope_insufficient",
    "member_role_insufficient",
    "recording_access_denied",
    "recording_operator_access_disabled",
    "impersonation_forbidden",
    "impersonation_expired",
    "operator_read_only",
    "break_glass_required",
    // ---- not found (404)
    "not_found",
    "voice_agent_not_found",
    "agent_version_not_found",
    "conversation_not_found",
    "channel_binding_not_found",
    "customer_order_not_found",
    "contact_list_not_found",
    // ---- conflict (409)
    "conflict",
    "live_version_seq_stale",
    "slug_taken",
    "address_already_bound",
    "sso_domain_already_claimed",
    "entra_tenant_already_mapped",
    "do_not_call_entry_exists",
    "coupon_already_redeemed",
    // ---- domain state (422)
    "agent_version_not_ready",
    "voice_agent_paused",
    "account_suspended",
    "language_unavailable",
    "language_pack_incomplete",
    "acknowledgement_set_unavailable",
    "voice_does_not_speak_language",
    "extraction_failed",
    "build_failed",
    "connection_operation_not_allowed",
    "connection_secret_unresolvable",
    "tenant_resolution_failed",
    "campaign_window_closed",
    "contact_suppressed",
    // ---- billing (402 / 409)
    "credit_exhausted",
    "trial_exhausted",
    "hard_stop_reached",
    "budget_exceeded",
    "payment_declined",
    "payment_requires_action",
    "payment_requires_reauth",
    "payment_outcome_ambiguous",
    "payment_method_not_found",
    "currency_not_supported",
    // ---- rate limiting (429)
    "rate_limited",
    // ---- upstream (502 / 504)
    "upstream_failed",
    "upstream_timed_out",
    "model_pool_unavailable",
    "speech_provider_failed",
    "carrier_failed",
    "payment_provider_failed",
    // ---- internal (500)
    "internal_error",
    "audit_write_failed",
    /*
     * ADDED 2026-09-22 (P6-01), raised by P2.
     *
     * `packages/db`'s `DataIntegrityError` — a stored value that cannot be represented in
     * TypeScript without losing information, `BIGINT` money outside `Number.MAX_SAFE_INTEGER`
     * being the case that exists today — had no code of its own and shipped as
     * `internal_error`, carrying `{table, column, rowId}` only structurally. Design 5.1 says a
     * code is "stable, documented" and that every one is enumerated HERE, so P2 correctly
     * declined to add it from outside this package and left a note instead. This is the code.
     *
     * It stays a 500: the caller did nothing wrong and cannot fix it by retrying — the same row
     * will arrive the same way next time. What the distinct code buys is a consumer (and an
     * alert) able to tell "a row is unrepresentable" apart from every other internal failure,
     * without parsing a human message.
     */
    "data_integrity_out_of_range",
    /*
     * ADDED 2026-09-22 (identity entry points, P5-01's routes).
     *
     * `POST /v1/auth/signup` for an address that already has a person row. A 409. This IS an
     * enumeration oracle and it is the only one in the identity subsystem — accepted, rate
     * limited per source, and recorded as residual D3 in
     * `apps/api/src/identity/SECURITY-REVIEW.md` §9.1, which already names this code. It gets
     * its own code rather than a bare `conflict` so a console can say "log in instead" without
     * parsing a human message. Log-in never uses it: every log-in failure is
     * `credential_invalid`.
     */
    "email_taken",
    /*
     * ADDED 2026-09-22 (plan-002 RB-03, design §15.12.2, DD-17).
     *
     * Every code plan-002's Track C, account-creation, operator sign-in and impersonation units
     * raise, added ONCE here so eleven parallel units never contend on this file. Appended in
     * §15.12.2's order and never reordered; the HTTP status each one ships with is the one
     * §15.12.2 gives, and the unit named beside it is the one that raises it. A unit that needs
     * one more appends below this block and rebases.
     *
     * §15.12.2's `audit_missing` is deliberately NOT here: the staff pipeline already answers the
     * "handler succeeded, collector empty" case with `audit_write_failed` (500, logged as
     * `staff.audit_missing`; see `apps/api/src/staff/http/pipeline.ts`), and §15.12.2 says to
     * reuse an existing code rather than mint a second spelling of the same failure. RB-18 raises
     * `audit_write_failed` on the customer pipeline for the same case.
     */
    "country_unsupported", // 422 — RB-17
    "time_zone_invalid", // 400 — RB-17
    "contact_list_in_use", // 409 — RB-24
    "contact_source_unavailable", // 422 — RB-24
    "contact_source_too_many_rows", // 422 — RB-24, RB-27
    "campaign_not_found", // 404 — RB-25
    "campaign_state_conflict", // 409 — RB-25
    "campaign_calling_window_invalid", // 400 — RB-25
    "campaign_simultaneous_call_over_ceiling", // 422 — RB-25
    "survey_round_not_found", // 404 — RB-26
    "export_too_large", // 422 — RB-26, RB-27, RB-29
    "export_format_unavailable", // 422 — RB-26
    "do_not_call_entry_not_found", // 404 — RB-27
    "retention_option_invalid", // 400 — RB-28
    "person_data_request_term_invalid", // 400 — RB-28
    "optimisation_review_not_found", // 404 — RB-30
    "optimisation_finding_not_found", // 404 — RB-30
    "optimisation_review_state_conflict", // 409 — RB-30
    "optimisation_review_unavailable", // 422 — RB-30
    "return_path_invalid", // 400 — RB-23
    "step_up_required", // 403 — RB-09
    "totp_enrolment_not_started", // 409 — RB-09
    "impersonation_handoff_invalid", // 401 — RB-18
    /*
     * ADDED 2026-09-23 (plan-002 RB-42, design §15.6.11). 503.
     *
     * A test call or a text test needs the agent_runtime — the process that places and answers
     * conversations, assembles the VoiceAgent's prompt and talks to the model pool — and this
     * deployment has none (the runtime's composition root still refuses to start, and the GPU
     * pool cannot allocate; register 0d). `model_pool_unavailable` would name the wrong thing:
     * it is what a RUNNING runtime answers when the pool refuses, and a console that shows it
     * today would tell a customer the GPU is busy when no runtime exists at all. Refused before
     * any row is written, so nothing is recorded and nothing is billed.
     */
    "agent_runtime_unavailable", // 503 — RB-42
    /*
     * ADDED 2026-09-23 (plan-002 Wave 4, payment safety, register 0n). 409, retryable.
     *
     * A request carrying an `Idempotency-Key` whose first request is still running — or stopped
     * without recording an answer. The pipeline claims the key (`SET … NX`) before the handler
     * runs, so two simultaneous requests with one key can no longer both execute; the second gets
     * this. It is not `idempotency_key_reused_with_different_body` (same key, SAME body) and not a
     * refusal of the request: retrying later with the same key returns the first request's answer
     * once it is recorded. It must never be read as "the first request did not happen".
     */
    "idempotency_in_progress", // 409 — apps/api/src/http/pipeline.ts
    /*
     * ADDED 2026-09-23 (plan-003 RC-32, design §16.8, §16.18). Direct-to-Blob document upload.
     *
     * The wizard asks for a short-lived, single-file, write-only upload link per document
     * (`createAgentDocumentUpload`), puts the bytes straight into storage, confirms each one
     * (`confirmAgentDocument`, which re-reads and re-hashes the stored bytes before any row exists),
     * and only then starts the build (`startVoiceAgentBuild`). Appended in §16.18's order.
     */
    "build_already_started", // 409 — RC-32: the version's build is no longer waiting for documents
    "build_no_document", // 422 — RC-32: the template needs a document and none is confirmed
    "document_too_large", // 413 — RC-32: over WETALK_API_DOCUMENT_MAX_BYTE
    "document_type_unsupported", // 415 — RC-32: not a content type the factory reads
    "document_count_exceeded", // 422 — RC-32: over WETALK_API_DOCUMENT_MAX_COUNT for one version
    "document_mismatch", // 422 — RC-32: the stored bytes are not the declared size or SHA-256
    "upload_grant_expired", // 410 — RC-32: the signed upload grant outlived its window
    "upload_grant_invalid", // 400 — RC-32: forged, altered, or for another document or account
    /*
     * ADDED 2026-09-23 (plan-003 RC-74, design §16.18). An operator assigns a WeTalk-held number
     * (decision D4 a) to a customer's VoiceAgent.
     */
    "number_bound_elsewhere", // 409 — RC-74: the number already routes to a VoiceAgent (this account's or another's)
    "number_not_held", // 422 — RC-74: not on WeTalk's carrier account, not in service, or not attached to the voice application
    /*
     * ADDED 2026-09-25 (customer-portal review #105, #106, #32). The owner's own lifecycle actions on a
     * VoiceAgent: pause and resume, archive, and try a failed build again.
     */
    "voice_agent_not_pausable", // 409 — #106: only a live VoiceAgent can be paused (never-built, building and failed ones cannot)
    "voice_agent_not_paused", // 409 — #106: resume asked of a VoiceAgent that is not paused by its owner
    "voice_agent_paused_by_operator", // 409 — #106: WeTalk paused it; only WeTalk resumes it
    "voice_agent_archive_refused", // 409 — #105: live, still reachable at a number, or its build is running
    "voice_agent_archived", // 409 — #105: the VoiceAgent is archived; nothing more is done to it
    "rebuild_refused", // 409 — #32: the newest version did not fail, so there is no failed build to try again
    "build_job_not_found", // 404 — #28: no such build in this account
    /*
     * ADDED 2026-09-25 (customer-portal review #75, fix round P). "Edit" opens the next version of a
     * built VoiceAgent from edited answers and builds it.
     */
    "build_in_progress", // 409 — #75: a build of this VoiceAgent is running; edit once it finishes
    "edit_unchanged", // 422 — #75: the edited answers are the version's own; there is nothing to build
    "edit_needs_no_build", // 422 — #75: only "Record calls" changed, which publishes without a build (PATCH)
    "document_location_mismatch", // 422 — #75: a kept document is not stored where the path helper names it
    /*
     * ADDED 2026-09-26 (plan-004 WC-40, design §17.10.6, K17, K22). The browser test, widget voice and
     * the Messaging page's allowed origins. Appended in §17.10.6's order, never reordered.
     */
    "all_lines_busy", // 503 — browser test, widget voice: the runtime's admission preview found no free line
    "webrtc_unavailable", // 503 — browser test: the WebRTC relay did not answer
    "widget_voice_disabled", // 404 — widget voice grant: the widget is disabled or its voice is off
    "widget_voice_unavailable", // 503 — widget voice grant: generic refusal (DD4-13)
    "origin_not_allowed", // 403 — widget voice grant, CORS: the embedding origin is not on the binding's list
    "proof_of_work_failed", // 400 — widget voice grant: the ALTCHA solution is missing, wrong or replayed
    "origin_invalid", // 400 — messaging origins, connect: not an origin normaliseOrigin accepts
    "origin_list_empty", // 422 — messaging enable / voice / origins: the binding would list no origin
    /*
     * ADDED 2026-09-26 (customer-portal review #197). Downloading the file a completed person-data
     * export wrote.
     */
    "person_data_request_not_found", // 404 — #197: no such request in this workspace
    "person_data_export_not_ready", // 409 — #197: a deletion, or an export that has not finished, has no file
    "person_data_export_expired", // 410 — #197: the export container's seven-day rule has removed the file
    // ---- plan-005 OP-11 (design §18.3.4, DD5-35): online payment, payouts, step-up — appended last.
    // `step_up_required` is the design's eighteenth code and already exists (RB-09, the operator's
    // step-up); it is reused, not spelled twice. Its status is the call site's (the customer step-up
    // answers 401, design §18.15.2).
    "customer_order_payment_link_active", // 409 — a link is creating or active for this order
    "customer_order_already_paid", // 409 — the order is paid; nothing to send, cancel or change
    "customer_order_not_paid_online", // 409 — a refund asked of an order no online payment paid
    "refund_exceeds_paid_amount", // 422 — the refund would take back more than was paid, net of refunds
    "refund_decision_required", // 422 — cancelling a paid order needs the refund decision stated
    "online_payment_unavailable", // 409 — the account's mode, the merchant binding or the offer rule refuses
    "payment_link_not_found", // 404 — the public landing page: no attempt for this code
    "payout_destination_invalid", // 422 — names the field; never echoes the IBAN
    "payout_destination_unverified", // 409 — pilot/live, or a payout, needs an operator-verified destination
    "step_up_unavailable", // 403 — an impersonated session cannot step up
    "maker_checker_violation", // 403 — the operator who created a payout run cannot approve it
    "safeguarding_shortfall", // 409 — collected funds plus the float do not cover everything owed
    "payout_run_open", // 409 — another payout run is created or approved
    "nothing_to_pay_out", // 409 — no account has an available balance to pay out
    "payout_state_conflict", // 409 — the payout or run is not in the state the transition needs
    "general_live_off", // 409 — mode live asked for while the general-live switch is off
    "regulatory_sign_off_missing", // 409 — the general-live switch needs a recorded regulatory sign-off
    // ---- plan-006 FIN-C1 (design §18.28.9, DD6-13): the payout run build marker — appended last, by
    // FIN-C1 alone (the plan-005 contract test pins this tail with slice(at, at + planned.length)).
    "payout_run_unbuilt", // 409 — the payout run was never finished building; the next create clears it
    "payout_run_building", // 409 — another payout run is being built right now (its build lock is held)
    // ---- plan-008 ST-DM (design §22.13.3): customer SIP trunks and on-demand outbound — appended last,
    // in §22.13.3's order, after plan-006's pinned tail.
    "caller_id_not_bound", // 422 — the caller-ID number is not a caller-ID number on a trunk bound to this VoiceAgent
    "destination_refused", // 422 — destinationVerdict refused (`detail.rule`)
    "sip_trunk_unverified", // 422 — on-demand or campaign through an unverified trunk
    "sip_trunk_disabled", // 422 — any leg through a disabled trunk
    "daily_ceiling_reached", // 429 — the runtime's daily outbound ceiling
    "sip_edge_unavailable", // 503 — the SIP edge (guard or SIP API) unreachable, or the vault unreadable
    "sip_trunk_in_use", // 409 — delete or number removal while a running campaign or a conversation in progress uses it
    "sip_trunk_number_taken", // 409 — the E.164 is an unreleased trunk number in any account
    "sip_trunk_check_cooldown", // 429 — the check's cooldown or daily ceiling
    "sip_trunk_inbound_off", // 422 — an armed inbound check while inbound is off (platform or trunk)
    // plan-008 review C1 (2026-09-30), appended last:
    "sip_trunk_limit_reached", // 409 — the account already holds WETALK_API_SIP_TRUNK_PER_ACCOUNT_MAXIMUM trunks
    // ---- design §26 (2026-10-02): the voice chooser's sample — appended last.
    "voice_sample_unavailable", // 404 — no sample line in the language, or the voice does not speak it; 503 — it cannot be produced now
    // ---- design §15.6.5 / §16.7.2 AS AMENDED 2026-10-04: a campaign asks its VoiceAgent's own
    // questionnaire, and a campaign can be ended or deleted — appended last.
    "campaign_question_not_accepted", // 422 — a question list for a VoiceAgent that asks its own questionnaire
    "returning_person_unavailable", // 422 — different_questions for a VoiceAgent that asks its own questionnaire
    "campaign_not_deletable", // 409 — the campaign has called somebody; end it instead
    // ---- ADDED 2026-10-05 (plan-014): organization, workspace, people and billing — design §27.9.5,
    // AS AMENDED by §27.23.2 / §27.23.3 (PD-2, PD-4). Appended last, once, in §27.9.5's order, by plan-014
    // REG (handed over from A1: this file feeds the generated ErrorCode enum, a single-writer artefact).
    // `fiscal_transmission_required` is deliberately ABSENT (PD-2: a document awaiting transmission is a
    // state, not an error); `issuer_unset` and `vat_rate_backdate_refused` close the block (PD-4). Reused,
    // not re-spelled: step_up_required, account_suspended, maker_checker_violation, credit_exhausted,
    // trial_exhausted, hard_stop_reached, budget_exceeded, idempotency_key_reused_with_different_body,
    // impersonation_forbidden, operator_read_only, rate_limited, export_too_large, slug_taken,
    // time_zone_invalid, sso_domain_already_claimed, country_unsupported.
    "scope_required", // 409 — A2: an account or workspace must be selected for this route
    "scope_changed", // 409 — A2: the request's scope generation is stale (another tab switched)
    "capability_missing", // 403 — A3: the principal lacks the named capability on the target
    "assurance_required", // 401 — A2/C2: the target's sign-in policy needs a challenge (`detail.challenge`)
    "network_not_allowed", // 403 — C2: console sign-in from outside the allowed networks
    "impersonation_scope_fixed", // 403 — A2: impersonation cannot switch account
    "revision_stale", // 409 — every administration write: the If-Match revision is not current
    "quote_stale", // 409 — D3a: the quote's generation moved
    "quote_expired", // 410 — D3a: the quote's lifetime passed
    "quote_mismatch", // 409 — D3a: the confirmation body differs from the quote
    "operation_conflict", // 409 — D3a/D4/E2/B4: another conflicting operation is open
    "operation_state_conflict", // 409 — every state machine: an illegal transition
    "payment_pending", // 409 — D3b/E5: a payment for this operation is still pending
    "entitlement_exhausted", // 402 — E3a/E4: the usage or resource entitlement is used up
    "concurrency_entitlement_reached", // 503 — E3a: the organization's concurrent-conversation entitlement is full
    "seat_limit_reached", // 409 — E4: creation or acceptance would exceed the seat entitlement
    "resource_limit_reached", // 409 — E4: creation would exceed the resource entitlement
    "allocation_exceeds_grant", // 422 — E3a: allocations add up to more than the grant
    "allocation_below_use", // 422 — E3a: an allocation below what the workspace already used
    "offer_not_sellable", // 422 — E1/E2: no approved values, or not offered to this account
    "commercial_policy_unset", // 503 — E1: a required owner input is unset; refused, never defaulted
    "custom_offer_policy_unset", // 503 — F3b: the custom-offer policy is unset
    "retention_policy_unset", // 503 — B4: the retention policy is unset
    "terms_revision_unset", // 503 — B1: the terms revision is unset
    "tax_policy_unset", // 503 — D5: a tax input (series formats) is unset
    "tax_rate_unset", // 503 — D5: no VAT rate is in force for the country at the tax point
    "account_closing", // 422 — B4: new purchases or paid activity refused while the account closes
    "account_suspended_nonpayment", // 422 — E5: paid activity refused while suspended for nonpayment
    "workspace_draining", // 422 — B3/B4: the workspace is draining
    "workspace_archived", // 422 — B4: the workspace is archived
    "workspace_creation_forbidden", // 403 — B3: the account's workspace creation policy refuses it
    "invitation_unavailable", // 410 — B2: a dead invitation token (no reason given)
    "invitation_identity_mismatch", // 403 — B2: signed in as someone other than the invited verified email
    "email_unverified", // 403 — B1/B2: creating an organization or accepting an invitation needs a verified email
    "last_owner_protected", // 409 — B2: the last active Owner cannot be removed or demoted
    "owner_change_forbidden", // 403 — B2: an Admin acting on an Owner
    "transfer_exceeds_available", // 422 — D4: money held or owed cannot move
    "payment_method_in_use", // 409 — D3b: the method is protected (default, auto-reload, mandate)
    "mandate_unavailable", // 422 — D3b: no valid mandate
    "auto_reload_limit_reached", // 429 — D3b: the daily auto-reload count is reached
    "refund_not_refundable", // 422 — D3b: nothing purchased remains unused
    "legal_profile_required", // 422 — D5a: a purchase needs the legal details first
    "tax_id_malformed", // 400 — PRIM-T: the local shape refuses the tax id
    "tax_id_verifier_unconfigured", // 503 — PRIM-T: no tax-id verifier is composed
    "document_not_issued", // 409 — D5b/D6: an estimated or awaiting document has no file
    "sign_in_policy_lockout", // 409 — C2: the preflight would lock out every Owner
    "sign_in_policy_domain_unverified", // 422 — C2: SSO-only saved as a draft until a domain is verified
    "last_sign_in_method", // 409 — C1: the last way to sign in cannot be removed
    "mfa_enrolment_required", // 403 — C1: the policy requires an authenticator before continuing
    "recovery_code_invalid", // 401 — C1: the recovery code is wrong or used
    "sign_in_code_invalid", // 401 — C1: the emailed sign-in code is wrong, used or expired
    "domain_check_unavailable", // 503 — C2: DNS could not be checked (never "not found")
    "suppression_unavailable", // 503 — B5: combined do-not-call could not be evaluated; outbound refused
    "share_unavailable", // 404 — B5: the share does not exist or is not visible
    "share_revoked", // 409 — B5: the share was revoked
    "closure_blocked", // 409 — B4: closing is blocked (`detail.blocker[]`)
    "terms_acceptance_required", // 409 — B1/E2: the configured terms revision is not accepted
    "operator_approval_required", // 202 as a state — F3a: queued for a second operator (maker/checker)
    "operator_capability_missing", // 403 — F3a: the operator lacks the capability
    "cost_price_backdate_refused", // 422 — CP: a past effective date for an already-priced part
    "status_text_refused", // 422 — ST-A: the publish denylist matched
    "probe_signature_invalid", // 401 — ST-A: a latency measurement with a bad HMAC
    "billing_assignment_missing", // 500 — D1b (DataIntegrityError): no assignment in force, never "the first"
    "issuer_unset", // 503 — D5b (PD-4): no financial document is issued while the issuer is unset
    "vat_rate_backdate_refused", // 422 — F3c (PD-4): a backdated VAT rate for a period that already has documents
    "domain_join_offer_unavailable", // 410 — INT (owner answer 47): the domain join offer no longer stands (no reason given)
    // ---- ADDED 2026-10-07 (plan-014 FIX2): appended last.
    "auth_session_superseded", // 409 — GET /v1/session: another tab's read just moved this token out of a revoked scope; read again
    // ---- ADDED 2026-10-09 (plan-015): the customer-portal design-sync gaps — design §28.4.4. Appended last, once,
    // in §28.4.4's order, by plan-015 REG (design §28.0.2 A3: this file feeds the generated ErrorCode enum, a
    // single-writer artefact; no later plan-015 unit edits it). The status is the error class the raising unit
    // builds (taxonomy.ts), recorded here beside each code. Reused, not re-spelled: validation_failed (with
    // `field`), voice_sample_unavailable, rate_limited, time_zone_invalid, not_found, capability_missing,
    // country_unsupported, sip_trunk_unverified, destination_refused.
    "channel_unit_limit_above_entitlement", // 422 — LIM: the VoiceAgent's line limit exceeds the organization's concurrent-conversation entitlement (`detail.entitlement_ceiling`)
    "name_taken", // 409 — NAM, QSET: a campaign, contact list or question set with the same name_key exists in its scope
    "campaign_started", // 409 — CMP: an edit after the campaign started
    "sip_trunk_inbound_only", // 422 — SIP: a dial, a check, "Call me" or a campaign on a trunk with no outbound details
    "forward_destination_refused", // 400 — AGC-API (and the runtime's log code): forward_to outside the reviewed countries, premium/emergency, or a WeTalk number
    "forward_unavailable", // 422 — AGC-API: a forward choice while WETALK_API_FORWARD_TO_PHONE=off
    "draft_base_changed", // 409 — DRF: publishing a draft whose base_agent_version_id is no longer live, without acknowledge_base_change
    "test_call_arm_not_found", // 404 — TC1: renewing a "My phone" arm that lapsed or was claimed
    "survey_question_set_not_ready", // 409 — QSET: a campaign names a set that is building, failed or archived
    "agent_version_pinned", // 409 — QSET: archiving a set (or its version) a scheduled or running campaign pins
    // ---- ADDED 2026-10-12 (owner: abuse limits; migration 0136): appended last.
    "signup_limit_reached", // 429 — POST /v1/auth/signup: the platform-wide self-serve sign-up limit for the rolling hour is reached
    "agent_build_limit_reached", // 429 — create, edit, rebuild: the organization started its hourly allowance of agent builds (Retry-After says when one frees)
    // ---- ADDED 2026-10-11 (plan-017): per-account talk rates and extra time — design §29.14 (§29.9.1, §29.9.2). Appended
    // last, once, in §29.14's order, by plan-017 REG (design §29.0.3 D8: this file feeds the generated ErrorCode enum, a
    // single-writer artefact; no later plan-017 unit edits it). The status is the error class the raising unit builds
    // (taxonomy.ts), recorded beside each code. Reused, not re-spelled: validation_failed (with `field`),
    // revision_stale, operation_conflict, capability_missing, rate_limited, configuration_missing. Not here:
    // `talk_rate_pin_missing` is a worker fault code (log line and gauge), never an API answer.
    "talk_rate_cutover_pending", // 409 — OPS-A: a talk rate change before 0139's cutover row exists (AP-5)
    "talk_rate_start_in_past", // 422 — OPS-A: a start before now, at submit and again at apply (never applied late)
    "talk_rate_change_pending", // 409 — OPS-A: a scheduled change already exists for one of the kinds
    "talk_rate_change_started", // 409 — OPS-A: a cancel after the change started (only superseded, Q8)
    "talk_rate_unclassified", // 422 — LIVE (admission refusal, never queued): a non-test conversation with no talk rate kind
    "talk_rate_missing", // 422 — LIVE (admission refusal, never queued): no talk rate resolves for the conversation's kind
    "overage_rate_retired", // 422 — EXT: a custom offer naming `overage_rate` (retired by Q7, ADR 0055)
    // ---- ADDED 2026-10-13 (plan-018): the "Customer support" agent_template — design §30.4.4 (as amended by §30.0.3 D9).
    // Appended last, once, in §30.4.4's order, by plan-018 DOM (wave 1a; later plan-018 codes go through INT). The status
    // is the error class the raising unit builds (taxonomy.ts), recorded beside each code. Reused, not re-spelled:
    // validation_failed, not_found, revision_stale, agent_build_limit_reached.
    "website_address_refused", // 400 — API-AG (create/edit, `detail.reason`), the factory's `address_refused` failure
    "website_address_missing", // 422 — API-AG: "Read the website again" on a live version with no website address
    "information_input_missing", // 400 — API-AG: no field of a required group was given (`detail.group`, `detail.field_code[]`)
    "information_input_too_large", // 413 — API-AG / agent-document: the pasted text is over its limit
    "website_reading_in_progress", // 409 — API-AG: a build of this VoiceAgent is queued or running
    "website_reading_not_found", // 404 — API-AG: the version was built without a website
    "website_page_not_found", // 404 — API-AG: no such page in the reading's snapshot
    "handoff_not_offered", // 422 — API-HO: the live version's engine does not declare `person_handoff`
    "handoff_unavailable", // 422 — API-HO (`detail.reason`); the runtime's log code for a refused hand_off_to_person
    "handoff_destination_refused", // 400 — API-HO (`detail.path_refusal`); the runtime's log code
    "handoff_language_unavailable", // 422 — API-HO: not one of the live version's languages, or its pack lacks `handoff.framing`
    "information_index_model_mismatch", // — RET: the index was built by another embedding artefact; the conversation fails loudly
    // ---- ADDED 2026-10-15 (plan-019): the wetalk help assistant — design §31.4.6, in its order (`../help/error.ts`'s
    // HELP_ERROR_CODE is the same list). Appended last, once, by plan-019 DOM (wave 1a). The status is the error class the
    // raising unit builds (taxonomy.ts), recorded beside each code. Reused, not re-spelled: validation_failed, conflict,
    // scope_changed, step_up_required, operator_read_only, draft_base_changed. (§31.4.6 also names
    // `agent_template_not_found` as existing; it is not in this list — an unknown template answers `validation_failed`
    // with `field: agent_template_code` today, and TPL adds the code through INT if its refusal of `help` needs one.)
    "help_unavailable", // 503 — API-HELP: help is off, or no live help version
    "help_unavailable_impersonating", // 403 — API-HELP: an operator is impersonating
    "help_grant_refused", // 403 — API-HELP: a grant refused for a reason the visitor cannot fix (unknown surface or language)
    "help_challenge_invalid", // 403 — API-HELP: the ALTCHA proof is missing, wrong, spent or expired
    "help_rate_limited", // 429 — API-HELP: a grant or request counter is spent; Retry-After set
    "help_request_not_found", // 404 — API-HELP: not found, or not this person's
    "help_conversation_mismatch", // 409 — API-HELP: the attached help conversation is another subject's
    "help_takeover_held", // 409 — OPR-LIVE: another operator holds it; `replace` not set
    "help_takeover_not_held", // 409 — OPR-LIVE: the caller does not hold it
    "help_state_illegal", // 409 — OPR-INBOX: the state move is not allowed from the current state (nextHelpRequestState)
    "help_documentation_building", // 409 — OPR-DOCS: a help build is running
    // ---- ADDED 2026-10-17 (owner decisions, the sign-up guard; ADR 0071, glossary §6.34).
    "identity_challenge_invalid", // 403 — the identity challenge (ALTCHA) on discover, log-in or sign-up is missing, wrong, spent or expired
    "bot_organization_ineligible", // 409 — the operator's bot organization purge: the organization is not one (a verified person, or something it holds)
    // ---- ADDED 2026-10-16 (plan-020): monthly plans — design §32.20, in its order (`../billing/subscription.ts`'s
    // SUBSCRIPTION_ERROR_CODE is the same list). Appended once by plan-020 DOM, after the sign-up guard's two codes since the
    // plan-020 INT merge of origin/main (2026-10-17), which shipped first (wave 1a; later plan-020 codes go
    // through INT). The status is the error class the raising unit builds (taxonomy.ts), recorded beside each code.
    // Reused, not re-spelled: configuration_missing, offer_not_sellable, quote_stale, quote_expired, mandate_unavailable,
    // operation_state_conflict, operation_conflict.
    "subscription_sale_closed", // 409 — SUB-KIND/API-SUB: purchase, upgrade or reactivation while commercialPolicy.subscriptionSale is off
    "subscription_tax_id_required", // 422 — SUB-KIND: the payer is not a business with a valid tax id (`detail.reason`: missing, invalid, unchecked, try_later)
    "subscription_consent_missing", // 422 — SUB-KIND: confirm with no consent, `agreed` false, or another text revision
    "subscription_change_unavailable", // 422 — SUB-KIND: a change quote while the renewal is unpaid (past_due, suspended)
    "offer_scope_ineligible", // 422 — SUB-KIND: a purchase quote on a dedicated balance (OD-7)
    "offer_term_unsupported", // 422 — SUB-KIND: an annual or trial revision (OA-6, SUPPORTED_SUBSCRIPTION_TERM)
    "payment_method_in_use_by_subscription", // 409 — API-CARD: removing the card a running subscription charges
    "subscription_renewal_attempt_ceiling", // 409 — OPR-SUB/CHARGE: the period's merchant-initiated attempts reached the ceiling
    "subscription_renewal_unresolved", // 409 — OPR-SUB/API-CARD: an attempt's outcome is still being looked up
    "subscription_tax_treatment_invalid", // fault (500) — FEE/SUB-KIND: a plan fee resolved to a consumer treatment
    // ---- ADDED 2026-10-19 (plan-021 API-CFG): the pass-target save's refusals — ED-32, design §33.13.1, in
    // `PASS_TARGET_REFUSAL`'s order (`../agent/voice-agent-pass.ts`). Reused, not re-spelled: validation_failed,
    // voice_agent_not_found, live_version_seq_stale, agent_version_not_ready.
    "pass_target_self", // 422 — the list names the VoiceAgent itself
    "pass_target_not_found", // 422 — another workspace, another account or a deleted VoiceAgent: one code, nothing leaks
    "pass_target_archived", // 422 — the target is archived
    "pass_target_cannot_receive", // 422 — the target's agent_template may not receive a pass (survey, help)
    "pass_source_cannot_pass", // 422 — the source's agent_template may not pass (survey, help)
    "pass_target_audience_crossed", // 422 — an external VoiceAgent to an internal one, or the reverse
    "pass_target_limit_exceeded", // 422 — more than VOICE_AGENT_PASS_TARGET_LIMIT entries (`detail.limit`)
    "pass_target_duplicate", // 422 — one VoiceAgent listed twice
    // ---- ADDED 2026-10-21 (plan-025, design §36.4, §36.10; ADR 0077): the help delegation and the account action check.
    // Reused, not re-spelled: scope_changed, help_unavailable_impersonating.
    "help_delegation_refused", // 403 — a delegated request off the read registry, not a GET, or account reading off
    "help_delegation_invalid", // 401 — an unknown, expired or malformed help delegation
    "help_action_unavailable", // 403 — a help action header while account actions are off, or not a person's own cookie
    "help_action_stale", // 409 — the help action record is missing, expired, used or does not match the request
    // ---- ADDED 2026-10-25 (plan-028, design §39.2, §39.5; ADR 0080): Google and Microsoft sign-in's address confirmation.
    "provider_code_invalid", // 401 — the emailed code for a held Google or Microsoft sign-in is wrong
    "provider_sign_in_expired", // 409 — the held sign-in is gone (expired, spent, or its attempts used): start again
    "provider_code_limit", // 409 — no more codes may be sent for this held sign-in
    "provider_sign_in_refused", // 409 — the held sign-in ended without a sign-in (the reason is logged, never sent)
    // ---- ADDED 2026-10-28 (plan-029 API-PROT, API-BIND): caller protection, blocked numbers and the access list — design
    // §40.5, §40.19.1–§40.19.4, in refusal order. The four `@wetalk/db` PartyWriteRefusedError reasons (CP-SEAM) are
    // answered under their own names. Reused, not re-spelled: validation_failed (with `field` and `detail.code`),
    // not_found, revision_stale, cursor_invalid.
    "caller_protection_unavailable", // 422 — WETALK_API_CALLER_PROTECTION=off: a caller-protection or blocked-number write
    "blocked_number_limit_reached", // 422 — 5,000 active blocked numbers in the workspace (CP-SEAM)
    "block_unavailable", // 422 — "Block this number" on a conversation that kept no digest: withheld, web, or removed (CP-SEAM)
    "access_list_unavailable", // 422 — WETALK_API_ACCESS_LIST=off: an access-list write
    "assurance_level_unavailable", // 422 — an assurance level (or signed visitors) whose release switch is not on
    "access_entry_exists", // 409 — the same identity is already on the VoiceAgent's list (CP-SEAM)
    "access_entry_limit_reached", // 422 — 500 entries on the VoiceAgent's list (CP-SEAM)
    "access_list_required", // 422 — API-BIND: a new binding of an internal VoiceAgent whose access list is not enabled (`detail.voice_agent_id`)
    // ---- ADDED 2026-10-28 (campaign fix-up; ADR 0045): a campaign's simultaneous_call above its VoiceAgent's own line limit.
    "campaign_simultaneous_call_over_voice_agent_limit", // 422 — estimate, create, or an edit of simultaneous_call
    // ---- ADDED 2026-10-28 (campaign fix-up, the workspace rung; 0089): simultaneous_call above the workspace's own line limit.
    "campaign_simultaneous_call_over_workspace_limit", // 422 — estimate, create, or an edit of simultaneous_call
    // ---- ADDED 2026-10-28 (plan-030 API-Q, design §41.5, §41.7; glossary §6.45; ADR 0084 proposed): the survey question
    // editor's questionnaire document routes, the edit route and the build start. Reused, not re-spelled: not_found (the
    // editor switched off), validation_failed, voice_agent_not_found, voice_agent_archived, rate_limited.
    "not_a_survey", // 422 — the questionnaire document of a VoiceAgent whose agent_template is not the survey
    "questionnaire_base_stale", // 409 — If-Match is not the editable version (`detail.editable`), or a concurrent insert
    "questionnaire_invalid", // 422 — the validator's problems (`detail.problem[]`, `detail.warning[]`); nothing saved
    "survey_rule_unavailable", // 422 — a questionnaire using rules (wetalk-survey-json-v3 keys) while rules are not available
    "survey_fact_in_questionnaire", // 422 — an Edit changing who runs the survey or its subject while the questions are carried
    // ---- ADDED 2026-10-28 (plan-032 DOM-OR, design §43.9; ADR 0086 proposed): the optimisation review engine. Reused, not
    // re-spelled: optimisation_review_unavailable (422, `field` = the unavailable reason, now also not_available_yet and
    // not_offered), optimisation_review_state_conflict, agent_version_not_ready.
    "optimisation_review_already_open", // 409 — the VoiceAgent already has a review idle or running (optimisation_review_one_open)
    "optimisation_finding_not_applicable", // 422 — `accepted` on an advice finding: the owner changes it by hand
    // ---- ADDED 2026-10-29 (plan-029 PC-SEAM, API-CONF, API-PRICE; design §40.19.3, §40.19.5, §40.25.1; ADR 0082
    // proposed): caller confirmation's settings, passcodes and the confirmation code price, in refusal order. Reused, not
    // re-spelled: validation_failed, not_found, revision_stale, assurance_level_unavailable, step_up_required.
    "party_confirmation_unavailable", // 422 — WETALK_API_PARTY_CONFIRMATION=off: a confirmation setting write
    "capability_not_protectable", // 422 — a capability the agent_template does not declare (`detail.capability`)
    "method_unavailable", // 422 — a minimum whose switch is off, or "signed visitor only" with a voice binding (`detail.reason`)
    "confirmation_code_price_unset", // 422 — an SMS-code method or access level while no confirmation code price is in force
    "passcode_unavailable", // 422 — a passcode set while WETALK_API_PARTY_CONFIRMATION=off
    "confirmation_code_price_unchanged", // 422 — the price asked is the one already in force at the start
    "confirmation_code_price_in_past", // 422 — a confirmation code price never starts in the past
    "confirmation_code_price_overlap", // 409 — a scheduled change already exists (cancel it first), or a concurrent change
    "confirmation_code_price_started", // 422 — only a confirmation code price that has not started may be cancelled
    "campaign_field_locked", // 409 — 2026-10-30: an edit of a started campaign's contact list or start (the rest may change)
    "campaign_finished", // 409 — 2026-10-30: an edit of a finished or ended campaign
    // ---- ADDED 2026-11-01 (plan-034 DOM, design §45.7.5, §45.31; ADRs 0089, 0090 proposed; glossary §6.49, §6.50): the
    // wetalk app's sign-in, the mobile app surface and push, in this order. The status is the error class the raising
    // unit builds (taxonomy.ts). Reused, not re-spelled: authentication_required (an app bearer with a cookie, or no app
    // bearer where one is required), validation_failed (a push token's shape), scope_changed, assurance_required.
    "mobile_surface_refused", // 403 — the app bearer reached an operation not on MOBILE_APP_OPERATION (`field` = the operationId)
    "app_authorization_code_invalid", // 400 — the app authorization code is unknown, spent, expired or its verifier does not match
    "app_sign_in_unavailable", // 422 — WETALK_API_APP_SIGN_IN=off: the mint and the exchange
    "app_sign_in_refused", // 403 — an operator acting as the person, a person not active or an unmet assurance (`field` = the reason)
    "app_push_unavailable", // 422 — WETALK_API_APP_PUSH=off: putAppPushRegistration (design §45.31)
];
const MEMBERSHIP = new Set(ERROR_CODE);
/** Narrows an arbitrary string to a documented code. */
export function isErrorCode(value) {
    return MEMBERSHIP.has(value);
}
//# sourceMappingURL=code.js.map